import { Link } from "react-router-dom";
import { Linkedin, Mail, Phone, ArrowRight } from "lucide-react";
import { BRAND, STATS } from "@/lib/site";

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="container-x py-16 md:py-24">
      <div className="grid md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-7">
          <span className="eyebrow">About Swift Financial</span>
          <h1 className="mt-4 h-display text-4xl sm:text-5xl lg:text-6xl">
            Partners in Growth — since {BRAND.founded}.
          </h1>
          <p className="mt-6 text-brand-sage text-lg leading-relaxed max-w-xl">
            Swift Financial Solutions is a Bangalore-based loan facilitation firm
            founded by {BRAND.founderName}. We sit on the borrower's side of the
            table — pre-vetting files, negotiating with 15+ banks and NBFCs,
            and structuring debt that scales with your goals.
          </p>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {STATS.map((s, i) => (
              <div key={i} data-testid={`about-stat-${i}`} className="card-soft p-5">
                <div className="font-outfit font-extrabold text-2xl text-brand-forest">{s.value}</div>
                <div className="mt-1 text-xs uppercase tracking-[0.16em] text-brand-sage">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="mt-10 prose-invert">
            <h3 className="font-outfit font-bold text-2xl text-brand-forest">Why borrowers choose us</h3>
            <ul className="mt-4 space-y-3 text-brand-forest/85 text-base">
              <li className="flex gap-3"><span className="text-brand-terracotta">—</span> One advisor across home, business, LAP/LRD and project finance.</li>
              <li className="flex gap-3"><span className="text-brand-terracotta">—</span> Transparent fees, no banker-side incentives — only client-aligned advice.</li>
              <li className="flex gap-3"><span className="text-brand-terracotta">—</span> 15+ lender partnerships across nationalised banks, private banks and NBFCs.</li>
              <li className="flex gap-3"><span className="text-brand-terracotta">—</span> Referral-led, reputation-driven — every relationship begins with trust.</li>
            </ul>
          </div>
        </div>

        {/* founder card */}
        <aside className="md:col-span-5 md:sticky md:top-28">
          <div className="card-soft overflow-hidden">
            <div className="relative">
              <img
                src={BRAND.founderPhotos.editorial}
                alt={BRAND.founderName}
                data-testid="founder-photo"
                className="w-full aspect-[4/5] object-cover object-center"
              />
              <div className="absolute top-4 left-4 rounded-full bg-brand-gold px-3 py-1 text-xs font-semibold text-brand-forest">
                Bangalore
              </div>
            </div>
            <div className="p-6">
              <div className="text-xs uppercase tracking-[0.18em] text-brand-terracotta">Founder</div>
              <h3 className="mt-1 font-outfit font-extrabold text-2xl text-brand-forest">{BRAND.founderName}</h3>
              <p className="mt-1 text-sm text-brand-sage">{BRAND.founderTitle}</p>
              <div className="mt-5 space-y-2.5 text-sm">
                <a href={`tel:${BRAND.phoneE164}`} className="flex items-center gap-3 text-brand-forest hover:text-brand-terracotta">
                  <Phone className="h-4 w-4" /> {BRAND.phone}
                </a>
                <a href={`mailto:${BRAND.email}`} className="flex items-center gap-3 text-brand-forest hover:text-brand-terracotta">
                  <Mail className="h-4 w-4" /> {BRAND.email}
                </a>
                <a href={BRAND.linkedin} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-brand-forest hover:text-brand-terracotta">
                  <Linkedin className="h-4 w-4" /> LinkedIn
                </a>
              </div>
              <Link to="/apply" data-testid="about-apply-btn" className="btn-primary w-full mt-6">
                Talk to Raj <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </aside>
      </div>

      {/* mission strip */}
      <div className="mt-20 grid md:grid-cols-3 gap-5">
        {[
          { t: "Mission", d: "Make institutional debt accessible, transparent and fairly priced for every Bangalore borrower." },
          { t: "Approach", d: "Pre-vet first, submit once. We protect your file from rejections that hurt your credit profile." },
          { t: "Promise", d: "From dream to deed — we walk every step with you, even after the disbursal hits your account." },
        ].map((b, i) => (
          <div key={b.t} data-testid={`about-block-${i}`} className="card-soft p-7">
            <div className="font-outfit font-semibold text-brand-terracotta uppercase tracking-[0.18em] text-xs">{b.t}</div>
            <p className="mt-3 font-outfit font-bold text-xl text-brand-forest leading-snug">{b.d}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
